import { useEffect, useState } from 'react';
import { InlineAlert } from '../../ui';
import { formatDuration } from '../../lib/format';
import { useT } from '../../i18n/i18n.tsx';
import type { RequestErrorCopy } from './requestErrors';

/**
 * DESIGN.md §9.3 [Requests] `RateLimitNotice` — the guest-facing surface for every
 * `describeRequestError()` result that carries a `retryAfterSec` (429 `RATE_LIMITED`, 403
 * `DEVICE_BLOCKED`): SPEC AC-REQ-6's per-device quota and the coarse `global::rate-limit`
 * middleware alike. Ticks its own countdown from the moment it mounts/receives a new
 * `retryAfterSec` rather than making `RequestPage` re-render every second just to keep one number
 * honest (DESIGN.md §6.4 "stale is stated, never implied" — the same principle applied to a
 * countdown instead of a data-age timestamp). Reuses `formatDuration` (the app's one `m:ss` rule,
 * DESIGN.md §7) rather than a bespoke "N minutes" string, so a 15-minute device block and a
 * 5-minute request-queue cooldown both read exactly like every other duration in this app.
 *
 * MVP-565: stays `InlineAlert`, and it is the closest call on the guest surface. It is not a
 * screen-level notice — it renders inside `RequestSheet`'s form, immediately above the submit
 * button, and what it says is why THAT button will not work yet and when it will. Floating it to
 * the top of the window would leave the customer pressing submit with the reason somewhere else on
 * screen. A standing condition is still a caption when it is a caption on a control.
 */
export function RateLimitNotice({ copy }: { copy: RequestErrorCopy }) {
  const t = useT();
  const [remainingSec, setRemainingSec] = useState(copy.retryAfterSec ?? 0);

  useEffect(() => {
    setRemainingSec(copy.retryAfterSec ?? 0);
    if (!copy.retryAfterSec) return;
    const deadline = Date.now() + copy.retryAfterSec * 1000;
    const timer = setInterval(() => {
      setRemainingSec(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
    // ponytail: restarts the clock only when `retryAfterSec`'s VALUE changes, not on every re-render
    // of an equal value. Two consecutive attempts landing on the exact same remaining second would
    // not restart the countdown, but the server's own window math (request-guard.js's
    // `evaluateDeviceQuota`) always returns a strictly smaller number on a repeat attempt within the
    // same window, so that case does not occur in practice.
  }, [copy.retryAfterSec]);

  return (
    <InlineAlert variant={copy.tone}>
      <strong>{t(copy.titleKey, copy.titleParams)}.</strong> {t(copy.detailKey, copy.detailParams)}
      {remainingSec > 0 && (
        <span className="rate-limit-notice__countdown text-meta tabular-nums">
          {t('guest.rateLimit.tryAgainIn', { duration: formatDuration(remainingSec * 1000) })}
        </span>
      )}
    </InlineAlert>
  );
}
